import { cn } from "@/lib/utils";
import { isAfter, isBefore, sub } from "date-fns";
import { Ellipsis, Link, Plus, TrashIcon } from "lucide-react";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setDeleteProjectCandidateId,
  setEditProjectCandidateId,
  setShowAddEditProjectModal,
  setShowDeleteProjectModal,
} from "@/store/projectsSlice";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Pencil1Icon } from "@radix-ui/react-icons";
import { useNavigate, useSearchParams } from "react-router";
import { SearchInput } from "@/components/ui/search-input";
import { Tooltip } from "react-tooltip";
import { useProjectWithUsers } from "@/hooks/useProjectWithUsers";
import store from "@/store/store";
import { setUnauthorized } from "@/store/codebaseSlice";

const ProjectDropdownMenu = ({
  project,
  children,
  isOnlyProject,
  isOpen,
  setOpen,
}) => {
  const dispatch = useDispatch();

  const handleEditClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    dispatch(setEditProjectCandidateId(project.id));
    dispatch(setShowAddEditProjectModal(true));
    setOpen(false);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    dispatch(setDeleteProjectCandidateId(project.id));
    dispatch(setShowDeleteProjectModal(true));
    setOpen(false);
  };

  const handleOpenInNewTab = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(false);
    // Get base URL of window

    const baseURL = window.location.origin;
    window.open(`${baseURL}?projectId=${project.id}`, "_blank");
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        <DropdownMenuItem
          onClick={handleOpenInNewTab}
          className="text-xs cursor-pointer"
        >
          <Link className="mr-2 h-3 w-3" />
          Open in New Tab
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleEditClick}
          className="text-xs cursor-pointer"
        >
          <Pencil1Icon className="mr-2 h-3 w-3" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleDeleteClick}
          className="text-xs text-destructive hover:text-destructive cursor-pointer"
        >
          <TrashIcon className="mr-2 h-3 w-3" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface ProjectItemProps {
  project: any;
  selected: boolean;
  onClick: (id: string) => void;
  isOnlyProject: boolean;
}

function ProjectItem({
  project,
  selected,
  onClick,
  isOnlyProject,
}: ProjectItemProps) {
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  return (
    <div className="flex items-center w-full h-7 relative group">
      <button
        className={cn(
          "flex justify-between items-center p-2 rounded hover:bg-secondary-n-950  h-7 overflow-hidden whitespace-nowrap w-full",
          selected && "bg-secondary-n-950"
        )}
        onClick={() => onClick(project?.id)}
      >
        <div className="flex gap-2">
          <p
            data-tooltip-id={project?.id}
            data-tooltip-content={project?.name}
            data-tooltip-delay-show={1000}
            className={cn(
              "text-xs text-left w-44 overflow-hidden text-ellipsis",
              !selected && "text-secondary-n-400"
            )}
          >
            {project?.name}
          </p>
        </div>
      </button>

      <ProjectDropdownMenu
        project={project}
        isOnlyProject={isOnlyProject}
        isOpen={isDropdownOpen}
        setOpen={setDropdownOpen}
      >
        <button
          className={cn(
            "absolute right-2 flex group-hover:opacity-100 hover:text-secondary-n-200 hover:bg-secondary-n-800 size-6 items-center justify-center",
            !isDropdownOpen && "opacity-0",
            isDropdownOpen && "bg-secondary-n-800"
          )}
        >
          <Ellipsis className="size-4 text-secondary-n-400" />
        </button>
      </ProjectDropdownMenu>
      <Tooltip
        className="z-50 !w-52 text-wrap"
        style={{
          fontSize: "0.75rem",
        }}
        id={project?.id}
      />
    </div>
  );
}

export function ProjectNav() {
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProjectId = searchParams.get("project_id");
  const componentId = searchParams.get("componentId");
  const searchParamPrompt = searchParams.get("prompt");

  const projectIdsByLastUpdated: { id: string; most_recent_update: string }[] =
    useSelector((state: any) => state.supabaseStore.projectIdsByLastUpdated);
  const baseProjects = useSelector(
    (state: any) => state.supabaseStore.projects
  );
  const projectIdsByLastUpdatedLoadStatus = useSelector(
    (state: any) => state.supabaseStore.projectIdsByLastUpdatedLoadStatus
  );

  const currentProject = useProjectWithUsers();

  const projectList = useMemo(() => {
    return projectIdsByLastUpdated
      .map(({ id }) => baseProjects[id])
      .filter((project) => {
        return Boolean(project);
      });
  }, [projectIdsByLastUpdated, baseProjects]);

  const filteredProjects = useMemo(() => {
    return search
      ? projectList.filter((project) => {
          const keysToSearch = ["name", "repoPath"];

          for (const key of keysToSearch) {
            if (project[key].toLowerCase().includes(search.toLowerCase())) {
              return true;
            }
          }
        })
      : projectList;
  }, [search, projectList]);

  const recentProjects = useMemo(() => {
    return filteredProjects
      ?.map((project) => {
        return {
          ...project,
          // @ts-expect-error TS2532
          last_updated: projectIdsByLastUpdated.find(
            (lastUpdated) => lastUpdated.id === project.id
          ).most_recent_update,
        };
      })
      .filter((project) =>
        isAfter(new Date(project.last_updated), sub(new Date(), { days: 2 }))
      )
      .sort((a, b) =>
        isBefore(new Date(a.last_updated), new Date(b.last_updated)) ? -1 : 1
      );
  }, [projectList, projectIdsByLastUpdated, filteredProjects]);

  const allProjects = useMemo(() => {
    return recentProjects?.length
      ? filteredProjects?.filter(
          (project) =>
            !recentProjects.some((recent) => recent?.id === project?.id)
        )
      : filteredProjects;
  }, [recentProjects, filteredProjects]);

  const isOnlyOneProject = useMemo(() => {
    return !(projectList.length > 1);
  }, [projectList]);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    [setSearch]
  );

  const fullProjectsList = useMemo(() => {
    return projectIdsByLastUpdatedLoadStatus !== "succeeded" ? [] : projectList;
  }, [projectIdsByLastUpdatedLoadStatus, projectList]);

  const recentProject = useMemo(() => {
    return fullProjectsList?.[0];
  }, [fullProjectsList]);

  const handleChangeSelectedProject = useCallback(
    (projectId) =>
      setSearchParams((prev) => ({
        ...prev,
        project_id: projectId,
      })),
    [setSearchParams]
  );

  useEffect(() => {
    // Exit early if no projects are available
    if (!fullProjectsList?.length) {
      return;
    }

    // Check if selected project is in user's project list
    const isSelectedProjectInList = fullProjectsList.some(
      (project) => project.id === selectedProjectId
    );

    if (isSelectedProjectInList) {
      // User has access to this project, nothing to do
      return;
    } else if (selectedProjectId) {
      // User is trying to access a project not in their list
      store.dispatch(setUnauthorized(true));
      return;
    } else if (recentProject && !componentId && !searchParamPrompt) {
      // No project selected, default to most recent project
      setSearchParams((prev) => ({ ...prev, project_id: recentProject.id }));
    }
  }, [fullProjectsList, recentProject, selectedProjectId, componentId]);

  return (
    <div className="flex w-64 px-4 pt-4 flex-col gap-4 h-full overflow-y-auto hide-scrollbar">
      <div className="flex justify-between items-center mb-2">
        <p className="text-xs">Projects</p>
        <button
          onClick={() => {
            navigate("/project-create/workflow-select");
          }}
        >
          <Plus className="size-4 text-secondary-n-400 hover:text-secondary-n-200" />
        </button>
      </div>
      <div className="w-full">
        <SearchInput onChange={handleSearchChange} />
      </div>
      <div className="flex flex-col gap-4 flex-1 overflow-y-auto">
        {!!recentProjects?.length && (
          <>
            <p className="text-xs text-secondary-n-600">Recent</p>
            <div className="w-full flex flex-col gap-1">
              {recentProjects.map((project) => (
                <ProjectItem
                  key={project?.id}
                  project={project}
                  selected={currentProject?.id === project?.id}
                  isOnlyProject={isOnlyOneProject}
                  onClick={handleChangeSelectedProject}
                />
              ))}
            </div>
          </>
        )}
        {!!allProjects?.length && (
          <>
            <p className="text-xs text-secondary-n-600">All projects</p>
            <div className="w-full flex flex-col gap-1">
              {allProjects.map((project) => (
                <ProjectItem
                  key={project?.id}
                  project={project}
                  selected={currentProject?.id === project?.id}
                  isOnlyProject={isOnlyOneProject}
                  onClick={handleChangeSelectedProject}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
